import { useState, useRef } from "react";
import { UpdateUserData, useUsersApi } from "@/hooks/useUsersApi";
import { toastHandler } from "@/lib/ToastHandler";

interface UseProfileEditProps {
  userData: any;
  onDataUpdated: () => void;
}

export function useProfileEdit({
  userData,
  onDataUpdated,
}: UseProfileEditProps) {
  const { updateUser } = useUsersApi();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados para modo edición
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [newProfileImage, setNewProfileImage] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState<string>("");
  const [fileError, setFileError] = useState<string>("");

  // Función para validar extensión de archivo
  const validateFileExtension = (file: File): string => {
    const allowedExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some((ext) =>
      fileName.endsWith(ext),
    );

    if (!hasValidExtension) {
      return `Extensión incompatible`;
    }

    return ""; // Sin errores
  };

  // Función para convertir imagen a base64
  const convertImageToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  // Validación del nombre completo
  const validateFullName = (name: string): string => {
    if (!name.trim()) {
      return "El nombre completo es requerido";
    }

    if (name !== name.trim()) {
      return "El nombre no puede tener espacios al inicio o final";
    }

    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length < 2) {
      return "Debe ingresar al menos nombre y apellido";
    }

    const hasValidParts = nameParts.every((part) => part.length >= 2);
    if (!hasValidParts) {
      return "Cada nombre debe tener al menos 2 caracteres";
    }

    return ""; // Sin errores
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      // Validar extensión del archivo
      const validationError = validateFileExtension(selectedFile);
      setFileError(validationError);

      if (validationError) {
        setNewProfileImage(selectedFile);
      } else {
        setNewProfileImage(selectedFile);
      }
    }
  };

  const handleRemovePhoto = () => {
    setNewProfileImage(null);
    setFileError(""); // Limpiar error de archivo
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleEditMode = () => {
    setIsEditMode(true);
    const currentName =
      userData.auth0Profile?.name ||
      userData.auth0Profile?.nickname ||
      userData.name ||
      userData.full_name ||
      (userData.first_name && userData.last_name
        ? `${userData.first_name} ${userData.last_name}`
        : "") ||
      "";
    setEditedName(currentName);
    setNewProfileImage(null); // Reset image selection when entering edit mode
    setNameError(""); // Reset name error
    setFileError(""); // Reset file error
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedName("");
    setNewProfileImage(null);
    setNameError(""); // Reset name error
    setFileError(""); // Reset file error
  };

  // Manejar cambios en el nombre
  const handleNameChange = (value: string) => {
    setEditedName(value);
    // Limpiar error mientras el usuario escribe
    if (nameError) {
      setNameError("");
    }
  };

  // Validar nombre cuando pierde el foco
  const handleNameBlur = () => {
    const error = validateFullName(editedName);
    setNameError(error);
  };

  const handleSaveEdit = async () => {
    // Try to get user ID from multiple possible locations
    const userId =
      userData?.roleSpecificData?.id ||
      userData?.id ||
      userData?.supabaseId ||
      userData?.user_id;

    if (!userId) {
      console.error("No user ID available for update");
      toastHandler({
        description:
          "No se pudo identificar el usuario para actualizar el perfil",
        variant: "destructive",
        title: "Error",
      });
      return;
    }

    // Validar antes de enviar - try multiple sources for original name
    const originalName =
      userData.auth0Profile?.name ||
      userData.auth0Profile?.nickname ||
      userData.name ||
      userData.full_name ||
      (userData.first_name && userData.last_name
        ? `${userData.first_name} ${userData.last_name}`
        : "") ||
      "";
    const nameChanged =
      editedName &&
      editedName.trim() !== originalName.trim() &&
      editedName.trim() !== "";

    if (nameChanged) {
      const nameValidationError = validateFullName(editedName);
      if (nameValidationError) {
        setNameError(nameValidationError);
        return;
      }
    }

    try {
      setIsSaving(true);

      // Preparar los datos para la actualización
      const updateData: UpdateUserData = {};

      // Agregar campos de texto si han cambiado
      if (editedName && editedName !== originalName) {
        // Separar nombre completo en first_name y last_name
        const nameParts = editedName.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        updateData.first_name = firstName;
        updateData.last_name = lastName;
      }

      // Mantener datos existentes que no se están editando
      if (userData.phone) {
        updateData.phone = userData.phone;
      }
      if (userData.roles && userData.roles.length > 0) {
        updateData.role = userData.roles[0];
      }
      if (userData.brand_ids && userData.brand_ids.length > 0) {
        updateData.brand_ids = userData.brand_ids;
      }

      // Manejar imagen de perfil si se seleccionó una nueva
      if (newProfileImage) {
        try {
          const base64Image = await convertImageToBase64(newProfileImage);
          updateData.picture = base64Image;
        } catch (error) {
          console.error("Error converting image to base64:", error);
          toastHandler({
            description: "Error al procesar la imagen",
            variant: "destructive",
            title: "Error",
          });
          return;
        }
      }

      // Llamar a la API para actualizar el usuario
      await updateUser(userId, updateData);

      // Actualizar los datos locales y salir del modo edición
      await onDataUpdated(); // Recargar datos del usuario
      setIsEditMode(false);
      setEditedName("");
      setNewProfileImage(null);
    } catch (error) {
      console.error("Error saving user profile:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Validación para habilitar el botón de guardar
  const canSave = () => {
    const originalName =
      userData?.auth0Profile?.name ||
      userData?.auth0Profile?.nickname ||
      userData?.name ||
      userData?.full_name ||
      (userData?.first_name && userData?.last_name
        ? `${userData.first_name} ${userData.last_name}`
        : "") ||
      "";
    const nameChanged =
      editedName &&
      editedName.trim() !== originalName.trim() &&
      editedName.trim() !== "";
    const imageSelected = newProfileImage !== null;

    // Si hay cambios en el nombre, debe ser válido
    if (nameChanged) {
      const nameValidationError = validateFullName(editedName);
      if (nameValidationError) {
        return false; // No se puede guardar si hay errores de validación
      }
    }

    // Si hay un archivo seleccionado, debe ser válido
    if (imageSelected && fileError) {
      return false; // No se puede guardar si hay errores de archivo
    }

    return nameChanged || imageSelected;
  };

  return {
    // States
    isEditMode,
    editedName,
    newProfileImage,
    isSaving,
    nameError,
    fileError,
    fileInputRef,

    // Handlers
    handlePhotoChange,
    handleRemovePhoto,
    handleEditMode,
    handleCancelEdit,
    handleNameChange,
    handleNameBlur,
    handleSaveEdit,
    canSave,
  };
}
